import { useMemo, useState } from 'react';
import { useCanSeeWarehouses } from '../../store/auth';
import { useQuery } from '@tanstack/react-query';
import { useUrlString, useUrlBool } from '../../hooks/useUrlState';
import { Select, Input, Segmented, Modal, Button, StatCard, Counted, Pagination, CodeChip, EntityAvatar } from '../../ui';
import { Undo2, PackageCheck, Ban, IndianRupee } from 'lucide-react';
import { request } from '../../api/client';
import { fmtQty, isRedundantSku } from '../../lib/format';
import { formatIst } from '../../lib/time';
import { Pill } from '../../components/TwDataTable';
import { warehouseOption, WarehouseCode } from '../../lib/warehouseLabel';
import { ProductThumb } from '../../components/ProductThumb';
import { productImages } from '../../lib/imageUrls';
import { accentForProductName } from '../../lib/productAccent';
import { CopyValue } from '../../components/CopyValue';
import { ReturnDetailPanel } from '../returns/ReturnDetailPage';
import { inr as inrWhole } from '../../lib/format';

/**
 * RETURNED-STOCK REGISTER — every returned sub-lot (sellable AND held), traceable to its PRINTED (original)
 * batch, warehouse-wise. Printed-batch-first: users see the batch on the packet, not the internal RET- lot
 * (which stays a small "system lot" under it). Read-only; the backend scopes a WHM to their warehouse(s).
 * Data from GET /returns/received-stock (return_line_receipts, enriched).
 *
 * FLAT, NOT FOLDED. The register used to group by product + printed batch + warehouse and hide the facts
 * that actually identify a returned lot — which return brought it, which retailer sent it back, who booked
 * it in, where it landed — one click down inside an expander. Nearly every group held a single receipt, so
 * the fold bought nothing and cost a click on every row: the group line was a restatement of the one row
 * underneath it, and the two disagreed about what a row meant (a group carried a BREAKDOWN of conditions,
 * a receipt carries ONE). Each row is now the receipt itself and says everything about itself; the row
 * opens the return record rather than a copy of its own contents.
 */

// The shared formatter: a negative reads "-₹480", never "₹-480".
const inr = inrWhole;

// "2026-07-31" is a machine's way of writing a date. Split by hand rather than through dayjs: these are
// DATE columns with no time and no zone, and re-interpreting one as an instant shifts it a day either
// side of midnight (see the mysql2 `dateStrings` note on the stock pages).
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDate = (v: any) => {
  const d = v ? String(v).substring(0, 10) : null;
  if (!d) return null;
  const [y, m, dd] = d.split('-');
  return MONTHS[Number(m) - 1] ? `${dd} ${MONTHS[Number(m) - 1]} ${y}` : d;
};
/** Whole days since a timestamp — how long this stock has been sitting where it landed. */
const daysSince = (v: any) => {
  if (!v) return null;
  const d = Math.floor((Date.now() - new Date(v).getTime()) / 86400000);
  return Number.isFinite(d) && d >= 0 ? d : null;
};

/**
 * Condition → user-facing label, pill colour, and the RAIL the row wears.
 *
 * The condition was said once, in a pill in the middle of a wide row, so the register read as one
 * undifferentiated list: a batch that came back in perfect condition and one that came back expired
 * looked identical until you read across. It now colours the row's left edge, which is where the eye
 * starts — so "something is wrong with this one" lands before any reading happens. `rank` orders the
 * condition picker worst-last and decides which condition wins when one has to.
 */
const COND: Record<string, { label: string; pill: string; rail: string; wash: string; rank: number }> = {
  RESALABLE:   { label: 'Sellable',       pill: 'green',  rail: 'border-l-brand-500',   wash: 'from-brand-500/[0.06]',   rank: 0 },
  NEAR_EXPIRY: { label: 'Near expiry',    pill: 'lime',   rail: 'border-l-lime-500',    wash: 'from-lime-500/[0.07]',    rank: 1 },
  MISMATCH:    { label: 'Batch mismatch', pill: 'gold',   rail: 'border-l-harvest-400', wash: 'from-harvest-400/[0.09]', rank: 2 },
  DEFECTIVE:   { label: 'Defective',      pill: 'gold',   rail: 'border-l-harvest-500', wash: 'from-harvest-500/[0.09]', rank: 3 },
  DAMAGED:     { label: 'Damaged',        pill: 'orange', rail: 'border-l-warning-500', wash: 'from-warning-500/[0.07]', rank: 4 },
  BLOCKED:     { label: 'Hold / recall',  pill: 'purple', rail: 'border-l-violet-500',  wash: 'from-violet-500/[0.07]',  rank: 5 },
  EXPIRED:     { label: 'Expired',        pill: 'red',    rail: 'border-l-danger-500',  wash: 'from-danger-500/[0.07]',  rank: 6 },
};
const FALLBACK_COND = { label: '', pill: 'default', rail: 'border-l-line', wash: 'from-transparent', rank: -1 };
const condMeta = (c: string) => COND[c] ?? { ...FALLBACK_COND, label: c };
const condLabel = (c: string) => condMeta(c).label;

/**
 * WHERE THE UNITS ACTUALLY WENT — the stock status of the lot they landed in.
 *
 * This is NOT the condition beside it. The condition is a judgement a person made at the receiving bay;
 * the landed status is what the system then did with the goods. They usually agree, and the case worth
 * seeing is the one where they don't — stock booked in as damaged that is nevertheless sitting in
 * sellable stock, say. So the agreeing case is written quietly under the condition ("in sellable stock")
 * and only a DISAGREEMENT is raised into amber; see `EXPECTED_LANDING`.
 */
const LANDED: Record<string, { label: string }> = {
  AVAILABLE: { label: 'sellable stock' },
  QC_HOLD:   { label: 'QC hold' },
  BLOCKED:   { label: 'blocked stock' },
  EXPIRED:   { label: 'expired stock' },
};
/** The lot state each receiving condition is supposed to produce. Anything else is worth naming. */
const EXPECTED_LANDING: Record<string, string> = {
  RESALABLE: 'AVAILABLE', NEAR_EXPIRY: 'AVAILABLE',
  MISMATCH: 'QC_HOLD', DEFECTIVE: 'QC_HOLD', DAMAGED: 'QC_HOLD',
  BLOCKED: 'BLOCKED', EXPIRED: 'EXPIRED',
};

const TH = 'px-3 py-2.5 text-left type-overline text-muted';
const THR = `${TH} text-right`;
const TD = 'px-3 py-2.5 align-middle';
/** The amber the register uses everywhere it has to name a gap rather than print an em-dash. */
const GAP = 'type-caption text-warning-600 dark:text-harvest-300';

type Row = {
  id: string; qty: number; condition: string; sellable: boolean;
  productId: string; productName: string; sku: string | null;
  imageUrl: string | null; imageUrls: string[] | null;
  originalBatch: string | null; internalBatch: string | null; landedStatus: string | null; expDate: string | null;
  warehouseId: string; warehouse: string; warehouseCode: string | null; warehouseName: string | null;
  returnId: string; returnNo: string; returnDate: string | null;
  retailerId: string | null; retailer: string | null;
  receivedByName: string | null; receivedAt: string | null; value: number;
};


export function ReturnedStockPage() {
  const canSeeWarehouses = useCanSeeWarehouses();
  const { data, isLoading } = useQuery({
    queryKey: ['received-stock'],
    queryFn: () => request<{ rows: Row[]; truncated?: boolean }>({ url: '/returns/received-stock' }),
  });
  const rows = data?.rows ?? [];
  // Master warehouse ids for the filter capsule — best-effort (degrades to no capsule without WAREHOUSE.VIEW).
  const { data: whAll } = useQuery({ queryKey: ['warehouses-all'], queryFn: () => request<any[]>({ url: '/warehouses/my' }), enabled: canSeeWarehouses, staleTime: 5 * 60_000, retry: false });
  const masterIds = useMemo(() => new Set((whAll ?? []).filter((w: any) => w.isMaster).map((w: any) => String(w.id))), [whAll]);

  // Every filter in the query string, so Back restores the view and a link carries it.
  const [wh, setWh] = useUrlString<string | undefined>('wh', undefined);
  const [cond, setCond] = useUrlString<string | undefined>('cond', undefined);
  const [view, setView] = useUrlString<'all' | 'sellable' | 'held'>('view', 'all');
  const [untraced, setUntraced] = useUrlBool('untraced');
  const [q, setQ] = useUrlString('q');
  const [openReturn, setOpenReturn] = useState<string | null>(null);

  // Paging is local because the whole register is: the endpoint hands back its (capped) set in one call
  // and every filter above is applied in the browser, so a page here is a slice of what is already here.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const warehouses = useMemo(
    () => [...new Map(rows.map((r) => [r.warehouseId, r])).entries()]
      .map(([id, r]) => ({ id, code: r.warehouseCode, name: r.warehouseName ?? r.warehouse, isMaster: masterIds.has(String(id)) })),
    [rows, masterIds],
  );
  const conditions = useMemo(() => [...new Set(rows.map((r) => r.condition))].sort((a, b) => condMeta(a).rank - condMeta(b).rank), [rows]);

  // SCOPE = warehouse + condition + search. The sellable/held split is deliberately NOT applied here:
  // the KPI tiles are computed from this set, so choosing "Held" cannot zero the "Sellable" tile
  // beside it — the row stays a breakdown of the same scope while the table below narrows.
  const scoped = useMemo(() => rows.filter((r) => {
    if (wh && r.warehouseId !== wh) return false;
    if (cond && r.condition !== cond) return false;
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      const hay = `${r.productName} ${r.sku ?? ''} ${r.originalBatch ?? ''} ${r.internalBatch ?? ''} ${r.returnNo ?? ''} ${r.retailer ?? ''}`.toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  }), [rows, wh, cond, q]);

  const filtered = useMemo(() => scoped.filter((r) => {
    if (view === 'sellable' && !r.sellable) return false;
    if (view === 'held' && r.sellable) return false;
    if (untraced && r.originalBatch) return false;
    return true;
  }), [scoped, view, untraced]);

  /*
   * HELD FIRST, AND THE OLDEST HELD AT THE TOP.
   *
   * Held stock is the half of this register somebody still has to make a decision about, and the one
   * fact that decides how urgent that is, is how long it has been sitting — so within held, oldest
   * received leads. Sellable stock needs no decision, so it reads newest-first like any other log.
   */
  const sorted = useMemo(() => {
    const when = (r: Row) => String(r.receivedAt ?? r.returnDate ?? '');
    return [...filtered].sort((a, b) =>
      Number(a.sellable) - Number(b.sellable)
      || (a.sellable ? when(b).localeCompare(when(a)) : when(a).localeCompare(when(b)))
      || a.productName.localeCompare(b.productName));
  }, [filtered]);

  // Filters re-slice the list, so page 4 of the old result is not page 4 of the new one. Clamping rather
  // than resetting on every setter keeps a deep-linked ?page out of range from rendering an empty table.
  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const current = Math.min(page, pageCount);
  const paged = sorted.slice((current - 1) * pageSize, current * pageSize);

  const totalUnits = scoped.reduce((s, r) => s + r.qty, 0);
  const sellableUnits = scoped.filter((r) => r.sellable).reduce((s, r) => s + r.qty, 0);
  const heldUnits = totalUnits - sellableUnits;
  const totalValue = scoped.reduce((s, r) => s + r.value, 0);
  const heldValue = scoped.filter((r) => !r.sellable).reduce((s, r) => s + r.value, 0);
  // Receipts booked in with no printed batch: real stock that cannot be tied back to a packet.
  const untracedUnits = scoped.filter((r) => !r.originalBatch).reduce((s, r) => s + r.qty, 0);

  // Every filter change sends the reader back to the first page — the rows under them changed.
  const go = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(1); };
  // Clicking a row's condition pill REPLACES the condition filter (click the active one to clear),
  // which is what makes the pill a way in rather than a label.
  const pickCond = (c: string) => { setCond((cur) => (cur === c ? undefined : c)); setPage(1); };

  return (
    <div>
      {data?.truncated && (
        <div className="mb-2 rounded-control border border-warning-500/30 bg-warning-500/10 px-3 py-2 text-xs text-content">
          Showing the 5,000 most recent returned receipts — older ones aren’t listed here. Filter by warehouse / product to focus, or ask for full history export if you need everything.
        </div>
      )}
      {/*
        THREE THINGS WERE WRONG WITH THE WORDING, and "what is 30?" was the symptom of all three.

        1. ONLY THE FIRST TILE NAMED ITS UNIT. "Returned units 39" said what it counted; "Sellable
           (back in stock) 30" and "Held (not sellable) 9" did not, so the reader had to carry the
           unit across from a tile two cards away and hope it still applied. Each value now carries
           its own unit, which is the one place a number can never be separated from it.
        2. THE LABELS RESTATED THEMSELVES. "Sellable (back in stock)" says one fact twice — sellable
           IS back in stock — and so does "Held (not sellable)". The parenthesis cost a line and
           added nothing.
        3. THE HINT SLOT HELD FOUR DIFFERENT KINDS OF THING — a state ("all returns"), an action
           ("show these"), a bare gerund with no subject ("filtering") and a fact ("₹1,228 of it
           held"). On the three tiles that ARE controls it now says only what clicking does, in the
           app's standard phrasing; the fourth is not a control and carries a fact, which is why it
           reads differently.

        The three counts are a partition — 30 + 9 = 39 — so they visibly add up to the first tile.
      */}
      <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Returned, all conditions" value={<Counted n={totalUnits} one="unit" many="units" />} tone="neutral" icon={<Undo2 size={16} />}
          hint={view === 'all' ? 'showing all returns' : 'show all returns'}
          active={view === 'all'} onClick={() => go(setView)('all')} />
        <StatCard label="Back in sellable stock" value={<Counted n={sellableUnits} one="unit" many="units" />} tone="success" icon={<PackageCheck size={16} />}
          hint={view === 'sellable' ? 'showing these — click to clear' : 'show these'}
          active={view === 'sellable'} onClick={() => go(setView)(view === 'sellable' ? 'all' : 'sellable')} />
        <StatCard label="Held, cannot be sold" value={<Counted n={heldUnits} one="unit" many="units" />} tone="danger" icon={<Ban size={16} />}
          hint={view === 'held' ? 'showing these — click to clear' : 'show these'}
          active={view === 'held'} onClick={() => go(setView)(view === 'held' ? 'all' : 'held')} />
        <StatCard label="Value at dealer price" value={inr(totalValue)} tone="sky" icon={<IndianRupee size={16} />}
          hint={heldValue > 0 ? `${inr(heldValue)} of it is held stock` : 'none of it is held'} />
      </div>

      <div className="overflow-hidden rounded-card border border-line bg-surface shadow-e1">
        <div className="flex flex-wrap items-center gap-2 p-3">
          <Input placeholder="Search product / batch / return / retailer" aria-label="Search product / batch / return / retailer" value={q} onChange={(e) => go(setQ)(e.target.value)} style={{ width: 300 }} />
          {/* Hidden when the viewer has no warehouse access — an always-empty picker reads as broken. */}
          {canSeeWarehouses && (
            <Select allowClear placeholder="All warehouses" value={wh} onChange={go(setWh)} style={{ width: 220 }}
              options={warehouses.map((w) => ({ value: w.id, ...warehouseOption(w) }))} />
          )}
          <Select allowClear placeholder="Any condition" value={cond} onChange={go(setCond)} style={{ width: 190 }}
            options={conditions.map((c) => ({ value: c, label: condLabel(c) }))} />
          <Segmented value={view} onChange={(v) => go(setView)(v as any)}
            options={[{ label: 'All', value: 'all' }, { label: 'Sellable', value: 'sellable' }, { label: 'Held', value: 'held' }]} />
        </div>

        {/* THE GAP, NAMED, ABOVE THE ROWS IT IS ABOUT.
            It sat under the table, between the last row and the pager, where a reader who never
            scrolls to the bottom never learns that a quarter of these units cannot be traced at all.
            A caveat about a list belongs where the list is entered, not where it is left behind. The
            line IS the control that opens it, so it can be acted on rather than only noted. */}
        {untracedUnits > 0 && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-warning-500/25 bg-warning-500/[0.07] px-3 py-2 type-label text-warning-700 dark:text-harvest-300">
            <span>
              {fmtQty(untracedUnits)} returned {untracedUnits === 1 ? 'unit' : 'units'} came in with no printed batch — they can’t be matched to a packet, a recall list, or the invoice they went out on.
            </span>
            <button type="button" onClick={() => { setUntraced((v) => !v); setPage(1); }} className="font-semibold underline underline-offset-2 hover:no-underline">
              {untraced ? 'Show all returned stock' : 'Show them'}
            </button>
          </div>
        )}

        <div className="overflow-x-auto border-t border-line">
          {isLoading ? (
            <div className="space-y-2 p-3">{[...Array(6)].map((_, i) => <div key={i} className="h-14 animate-pulse rounded bg-surface-3" />)}</div>
          ) : sorted.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-subtle">No returned stock{q || wh || cond || untraced || view !== 'all' ? ' matches these filters' : ' yet'}.</div>
          ) : (
            <table className="w-full min-w-[1320px] border-collapse text-sm">
              <thead className="gb-thead-tint bg-surface-2">
                <tr>
                  <th className={TH}>Product</th>
                  <th className={TH}>Printed batch</th>
                  <th className={TH}>Expiry</th>
                  <th className={TH}>Warehouse</th>
                  <th className={TH}>Condition</th>
                  <th className={TH}>Return</th>
                  <th className={TH}>Retailer</th>
                  <th className={TH}>Received by</th>
                  <th className={THR}>Qty</th>
                  <th className={THR}>Value</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => {
                  const meta = condMeta(r.condition);
                  const landed = r.landedStatus ? LANDED[r.landedStatus] : null;
                  const misplaced = !!r.landedStatus && !!EXPECTED_LANDING[r.condition] && r.landedStatus !== EXPECTED_LANDING[r.condition];
                  const age = daysSince(r.receivedAt ?? r.returnDate);
                  return (
                    <tr
                      key={r.id}
                      /* The whole row opens the return it came in on — the one record that explains it.
                         The condition pill, the return number and the photo all stop the click first, so
                         each does its own thing instead of everything meaning "open the return". */
                      className={`cursor-pointer border-b border-line/70 border-l-[3px] ${meta.rail} bg-linear-to-r ${meta.wash} to-transparent to-45% hover:bg-surface-2`}
                      onClick={() => setOpenReturn(r.returnId)}
                    >
                      <td className={TD}>
                        <div className="flex items-center gap-2.5">
                          {/* The pack shot, openable. Two initials tell one row from the next; they do
                              not tell a receiver whether the thing on the rack is the thing in this row.
                              No photo on file → initials on the product's own packaging colour, the hue
                              it wears in the stock table and the valuation chart. */}
                          <ProductThumb name={r.productName} srcs={productImages(r)} hue={accentForProductName(r.productName)} size={34} />
                          <div className="min-w-0">
                            <div className="font-medium text-content">{r.productName}</div>
                            {r.sku && !isRedundantSku(r.sku, r.productName) && <div className="type-caption text-subtle">{r.sku}</div>}
                          </div>
                        </div>
                      </td>
                      <td className={TD}>
                        {r.originalBatch
                          ? <CopyValue value={r.originalBatch} label="Printed batch"><CodeChip>{r.originalBatch}</CodeChip></CopyValue>
                          /* Not an em-dash. Stock with no printed batch is stock nobody can match to a
                             packet, a recall list or the invoice it went out on — a gap worth naming. */
                          : <span className={GAP}>no batch printed</span>}
                        {/* Demoted on purpose: the internal lot is what the SYSTEM calls this stock,
                            never what is printed on the packet. Kept because it is the id support asks
                            for — on one line, because wrapped over two it read louder than the printed
                            batch above it, which is the one anybody actually uses. */}
                        {r.internalBatch && (
                          <div className="mt-0.5 whitespace-nowrap type-caption text-subtle">
                            system lot{' '}
                            <CopyValue value={r.internalBatch} label="System lot">
                              <span className="font-mono" title="Internal system lot — you don't need this; search by the printed batch above">{r.internalBatch}</span>
                            </CopyValue>
                          </div>
                        )}
                      </td>
                      {/* A lot with no expiry on file is not a blank — it is a lot whose shelf life
                          nobody can check, which is why it is named rather than left as an em-dash. */}
                      <td className={`${TD} whitespace-nowrap text-muted`}>
                        {fmtDate(r.expDate) ?? <span className={GAP}>no expiry on file</span>}
                      </td>
                      <td className={`${TD} whitespace-nowrap`}>
                        {r.warehouseCode
                          ? <span className="flex min-w-0 items-center gap-1.5"><WarehouseCode code={r.warehouseCode} /><span className="min-w-0 truncate type-label text-muted">{r.warehouseName}</span></span>
                          : <span className="text-muted">{r.warehouseName ?? r.warehouse}</span>}
                      </td>
                      <td className={TD}>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); pickCond(r.condition); }}
                          aria-pressed={cond === r.condition}
                          title={cond === r.condition ? `Showing only ${meta.label} — click to clear` : `Show only ${meta.label}`}
                          className={`rounded-pill transition-shadow hover:shadow-e1 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-500 ${cond === r.condition ? 'ring-2 ring-brand-500/40' : ''}`}
                        >
                          <Pill color={meta.pill}>{meta.label}</Pill>
                        </button>
                        {/* Where the units actually went. Quiet when it is what the condition implies;
                            amber when it is not, because that is the case somebody has to look at. */}
                        <div className={`mt-0.5 ${misplaced || !r.landedStatus ? GAP : 'type-caption text-subtle'}`}>
                          {!r.landedStatus ? 'no stock lot recorded'
                            : misplaced ? `but sitting in ${landed?.label ?? r.landedStatus}`
                            : `in ${landed?.label ?? r.landedStatus}`}
                        </div>
                      </td>
                      <td className={`${TD} whitespace-nowrap`}>
                        <button type="button" onClick={(e) => { e.stopPropagation(); setOpenReturn(r.returnId); }} className="font-medium text-brand-700 hover:underline dark:text-brand-400">{r.returnNo}</button>
                        <div className="type-caption text-subtle">
                          {r.receivedAt ? formatIst(r.receivedAt) : (fmtDate(r.returnDate) ?? <span className={GAP}>receipt date not recorded</span>)}
                        </div>
                        {/* A held quantity on its own says nothing about whether anyone is dealing with
                            it. Amber past a week — that is the line between a queue and a pile. */}
                        {age != null && !r.sellable && (
                          <div className={age > 7 ? GAP : 'type-caption text-subtle'}>{age === 0 ? 'held since today' : `held ${age}d`}</div>
                        )}
                      </td>
                      <td className={TD}>
                        {r.retailer
                          ? <span className="flex min-w-0 items-center gap-1.5"><EntityAvatar name={r.retailer} size={20} /><span className="min-w-0 text-muted">{r.retailer}</span></span>
                          : <span className="text-subtle">—</span>}
                      </td>
                      <td className={TD}>
                        {r.receivedByName
                          ? <span className="flex min-w-0 items-center gap-1.5"><EntityAvatar name={r.receivedByName} size={20} /><span className="min-w-0 text-muted">{r.receivedByName}</span></span>
                          : <span className="text-subtle">—</span>}
                      </td>
                      {/* Held quantity is the one that needs a decision, so it carries the danger colour
                          the "Held (not sellable)" tile above uses; sellable quantity is just stock. */}
                      <td className={`${TD} whitespace-nowrap text-right font-semibold tabular-nums ${r.sellable ? 'text-content' : 'text-danger-600'}`}>{fmtQty(r.qty)}</td>
                      <td className={`${TD} whitespace-nowrap text-right text-content tabular-nums`}>{inr(r.value)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {sorted.length > 0 && (
          <div className="flex items-center justify-end border-t border-line px-3 py-2.5">
            <Pagination
              size="small"
              current={current}
              pageSize={pageSize}
              total={sorted.length}
              showSizeChanger
              pageSizeOptions={[10, 20, 50, 100]}
              onChange={(p, ps) => { setPage(p); setPageSize(ps); }}
              showTotal={(t, [from, to]) => `${from}–${to} of ${t} returned ${t === 1 ? 'lot' : 'lots'}`}
            />
          </div>
        )}
      </div>
      <p className="mt-2 text-xs text-subtle">
        One row per returned lot. Printed batch = the number on the packet; the “system lot” under it is internal — you never need to type it, search by the printed batch, product, return, or retailer above. “Condition” is the call the warehouse made at the bay, and the line under it is the stock state those units are actually sitting in now. Click a photo to see the product full size, or a row to open its return.
      </p>

      {/* Same dialog the Returns register opens — one presentation for one panel. */}
      <Modal
        title="Return details"
        width={980}
        open={!!openReturn}
        onOpenChange={(o) => { if (!o) setOpenReturn(null); }}
        footer={<Button variant="default" onClick={() => setOpenReturn(null)}>Close</Button>}
      >
        {openReturn && <ReturnDetailPanel id={openReturn} onClosed={() => setOpenReturn(null)} />}
      </Modal>
    </div>
  );
}
